import { createContext, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
// firestore
import { signOut } from "firebase/auth";
import { auth } from "../services/firebase";
// utils
import axios from "../utils/axios";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";
// auth
import reducer, { initialAuthState, VERSION_CACHE_KEY, hasCachedVersion } from "../reducers/authReducer";
import actionTypes from "../common/actionTypes";

// auth constants
const FIREBASE_AUTH_URL = "/auth/getCustomToken";

const VersionStoreContext = createContext({
  ...initialAuthState,
});

const VersionStoreProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(reducer, initialAuthState);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const shopifySessionToken = searchParams.get("id_token");

    if (shopifySessionToken) {
      dispatch({
        type: actionTypes.SET_SHOPIFY_ID_TOKEN,
        payload: shopifySessionToken,
      });
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    if (state.shopifyIdToken) {
      axios
        .get(FIREBASE_AUTH_URL, {
          headers: {
            Authorization: state.shopifyIdToken,
          },
        })
        .then(
          ({
            data: {
              data: { token = null, timesactVersion = null },
            },
          }) => ({
            token,
            timesactVersion,
          })
        )
        .then(({ token, timesactVersion }) => {
          const cachedVersion = state.timesactVersion;

          try {
            localStorage.setItem(VERSION_CACHE_KEY, JSON.stringify(timesactVersion));
          } catch {
            // localStorage write failed — non-critical
          }

          if (hasCachedVersion && cachedVersion !== timesactVersion) {
            window.location.reload();
            return;
          }

          return dispatch({
            type: actionTypes.LOGIN_SUCCESS,
            payload: {
              timesactVersion,
              accessToken: token,
              isVersionLoaded: true,
            },
          });
        })
        .catch((error) => {
          dispatch({
            type: actionTypes.LOGIN_FAILURE,
            payload: error,
          });

          if (auth.currentUser?.uid) {
            signOut(auth);

            localStorage.clear();

            dispatch({ type: actionTypes.LOGOUT });
          }
        });
    }
  }, [state.shopifyIdToken]);

  const memoizedState = useMemo(() => state, [state]);

  return <VersionStoreContext.Provider value={memoizedState}>{children}</VersionStoreContext.Provider>;
};

export { VersionStoreContext, VersionStoreProvider };
